/**
 * Theme wishlist — localStorage, product-card toggles, page/drawer grids,
 * header bubble, and fly-to-heart animation (reuses fly-to-cart element).
 */

import { isMembersProductBlocked } from '@theme/members-gate';

const ZOH_WISHLIST_KEY = 'zoh-wishlist';
const WISHLIST_BUTTON_SELECTOR = '[data-zoh-product-wishlist]';
const WISHLIST_EVENT = 'zoh:wishlist:change';

/**
 * @param {Array<Record<string, string>>} items
 * @returns {Array<Record<string, string>>}
 */
function filterMembersWishlistItems(items) {
  if (!Array.isArray(items) || items.length === 0) return [];
  return items.filter((item) => !isMembersProductBlocked(item?.id));
}
/**
 * @returns {Array<Record<string, string>>}
 */
export function zohWishlistRead() {
  try {
    const raw = localStorage.getItem(ZOH_WISHLIST_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

/**
 * @param {Array<Record<string, string>>} items
 */
function zohWishlistWrite(items) {
  localStorage.setItem(ZOH_WISHLIST_KEY, JSON.stringify(items));
  document.dispatchEvent(
    new CustomEvent(WISHLIST_EVENT, {
      detail: { items, count: items.length },
    })
  );
}

/**
 * @param {string} productId
 * @returns {boolean}
 */
export function zohWishlistHas(productId) {
  return zohWishlistRead().some((item) => String(item.id) === String(productId));
}

/**
 * @param {HTMLButtonElement} button
 * @param {boolean} active
 */
function zohWishlistSyncButton(button, active) {
  button.toggleAttribute('data-active', active);
  button.setAttribute('aria-pressed', active ? 'true' : 'false');
  const labelAdd = button.getAttribute('data-label-add') || 'Add to wishlist';
  const labelRemove = button.getAttribute('data-label-remove') || 'Remove from wishlist';
  button.setAttribute('aria-label', active ? labelRemove : labelAdd);
}

/**
 * @param {string} productId
 * @param {boolean} active
 */
function syncAllMatchingButtons(productId, active) {
  document.querySelectorAll(WISHLIST_BUTTON_SELECTOR).forEach((node) => {
    if (!(node instanceof HTMLButtonElement)) return;
    if (String(node.getAttribute('data-product-id')) !== String(productId)) return;
    zohWishlistSyncButton(node, active);
  });
}

/**
 * @param {ParentNode} root
 */
function syncWishlistButtons(root = document) {
  root.querySelectorAll(WISHLIST_BUTTON_SELECTOR).forEach((node) => {
    if (!(node instanceof HTMLButtonElement)) return;
    const productId = node.getAttribute('data-product-id');
    if (!productId) return;
    zohWishlistSyncButton(node, zohWishlistHas(productId));
  });
}

/**
 * Fly product image to header heart — same fly-to-cart animation path as ATC.
 * @param {HTMLElement} source
 * @param {string} imageUrl
 */
function animateFlyToWishlist(source, imageUrl) {
  const destination =
    document.querySelector('[data-zoh-wishlist-icon]') ||
    document.querySelector('[data-zoh-wishlist-trigger]');

  if (!(destination instanceof Element) || !imageUrl) return;

  const fly = document.createElement('fly-to-cart');
  fly.classList.add('fly-to-cart--quick');
  fly.style.setProperty('background-image', `url(${imageUrl})`);
  fly.style.setProperty('--start-opacity', '0');
  // @ts-expect-error custom element props
  fly.source = source;
  // @ts-expect-error custom element props
  fly.destination = destination;
  document.body.appendChild(fly);
}

/**
 * @param {HTMLButtonElement} button
 * @param {{ skipFly?: boolean }} [options]
 */
function zohWishlistToggle(button, options = {}) {
  const productId = button.getAttribute('data-product-id');
  if (!productId) return;

  const items = zohWishlistRead();
  const index = items.findIndex((item) => String(item.id) === String(productId));

  if (index >= 0) {
    items.splice(index, 1);
    zohWishlistWrite(items);
    syncAllMatchingButtons(productId, false);
    return;
  }

  const image = button.getAttribute('data-product-image') || '';
  items.push({
    id: productId,
    handle: button.getAttribute('data-product-handle') || '',
    title: button.getAttribute('data-product-title') || '',
    url: button.getAttribute('data-product-url') || '',
    image,
    price: button.getAttribute('data-product-price') || '',
    compare_at_price: button.getAttribute('data-product-compare-price') || '',
  });

  zohWishlistWrite(items);
  syncAllMatchingButtons(productId, true);

  if (!options.skipFly) {
    animateFlyToWishlist(button, image);
  }
}

/**
 * @param {string} productId
 */
function zohWishlistRemove(productId) {
  const items = zohWishlistRead().filter((item) => String(item.id) !== String(productId));
  zohWishlistWrite(items);
  syncAllMatchingButtons(productId, false);
}

function updateHeaderBubble() {
  const count = zohWishlistRead().length;
  const triggers = document.querySelectorAll('[data-zoh-wishlist-trigger]');

  triggers.forEach((trigger) => {
    if (!(trigger instanceof HTMLElement)) return;
    trigger.toggleAttribute('data-has-items', count > 0);

    const bubble = trigger.querySelector('[data-zoh-wishlist-bubble]');
    const bubbleCount = trigger.querySelector('[data-zoh-wishlist-bubble-count]');

    if (bubble instanceof HTMLElement) {
      bubble.classList.toggle('visually-hidden', count === 0);
      bubble.setAttribute('aria-hidden', count === 0 ? 'true' : 'false');
    }

    if (bubbleCount instanceof HTMLElement) {
      bubbleCount.textContent = count > 0 ? (count < 100 ? String(count) : '99+') : '';
    }
  });
}

/** @type {Array<Record<string, string>> | null} */
let sharedWishlistItems = null;

/**
 * @param {Record<string, string>} item
 * @param {{ hideRemove?: boolean }} [options]
 * @returns {HTMLLIElement}
 */
function createWishlistCard(item, options = {}) {
  const li = document.createElement('li');
  li.className = 'zoh-wishlist-card';
  li.dataset.productId = item.id || '';

  const removeLabel =
    (window.Theme && window.Theme.translations && window.Theme.translations.wishlist_remove) ||
    'Remove from wishlist';

  const compareHtml = item.compare_at_price
    ? `<span class="zoh-wishlist-card__compare">${item.compare_at_price}</span>`
    : '';

  const imageHtml = item.image
    ? `<img src="${item.image}" alt="${escapeHtml(item.title || '')}" width="688" height="860" loading="lazy">`
    : '';

  const removeHtml = options.hideRemove
    ? ''
    : `
    <button
      type="button"
      class="zoh-wishlist-card__remove"
      data-zoh-wishlist-remove
      data-product-id="${escapeAttr(item.id || '')}"
      aria-label="${escapeAttr(removeLabel)}"
    >
      <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">
        <path fill="currentColor" d="M11.1765 12.025C11.4108 12.2594 11.7907 12.2594 12.025 12.025C12.2594 11.7907 12.2594 11.4108 12.025 11.1765L8.84931 8.00078L12.025 4.82505C12.2594 4.59073 12.2594 4.21083 12.025 3.97652C11.7907 3.7422 11.4108 3.7422 11.1765 3.97652L8.00078 7.15225L4.82505 3.97652C4.59073 3.7422 4.21083 3.7422 3.97652 3.97652C3.7422 4.21083 3.7422 4.59073 3.97652 4.82505L7.15225 8.00078L3.97652 11.1765C3.7422 11.4108 3.7422 11.7907 3.97652 12.025C4.21083 12.2594 4.59073 12.2594 4.82505 12.025L8.00078 8.84931L11.1765 12.025Z"></path>
      </svg>
    </button>`;

  li.innerHTML = `
    ${removeHtml}
    <a class="zoh-wishlist-card__media" href="${escapeAttr(item.url || '#')}">${imageHtml}</a>
    <div class="zoh-wishlist-card__content">
      <a class="zoh-wishlist-card__title" href="${escapeAttr(item.url || '#')}">${escapeHtml(item.title || '')}</a>
      <p class="zoh-wishlist-card__price">
        <span>${escapeHtml(item.price || '')}</span>
        ${compareHtml}
      </p>
    </div>
  `;

  return li;
}

/**
 * @returns {string}
 */
function getWishlistShareBaseUrl() {
  const pageUrl = window.Theme?.settings?.wishlist_page_url;
  if (pageUrl) {
    return new URL(pageUrl, window.location.origin).href;
  }
  if (document.querySelector('[data-zoh-wishlist-page]')) {
    return `${window.location.origin}${window.location.pathname}`;
  }
  // Drawer-only stores: share against the current shop URL so the link still works.
  return `${window.location.origin}/`;
}

/**
 * @param {Array<Record<string, string>>} items
 * @returns {string}
 */
function buildWishlistShareUrl(items) {
  const base = getWishlistShareBaseUrl();
  if (!base) return '';

  const handles = items.map((item) => item.handle).filter(Boolean);
  const url = new URL(base);
  if (handles.length) {
    url.searchParams.set('share', handles.join(','));
  }
  return url.toString();
}

/**
 * @returns {string}
 */
function getWishlistShareMessage() {
  return (
    (window.Theme && window.Theme.translations && window.Theme.translations.wishlist_share_message) ||
    'Check out my wishlist:'
  );
}

/**
 * Official web share endpoints (Instagram has none — copy-link only).
 * @param {string} platform
 * @param {string} shareUrl
 * @param {string} message
 * @returns {string}
 */
function buildSocialShareHref(platform, shareUrl, message) {
  const text = `${message} ${shareUrl}`;

  switch (platform) {
    case 'whatsapp':
      return `https://wa.me/?text=${encodeURIComponent(text)}`;
    case 'facebook':
      return `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
    case 'twitter':
      return `https://twitter.com/intent/tweet?text=${encodeURIComponent(message)}&url=${encodeURIComponent(shareUrl)}`;
    case 'pinterest':
      return `https://pinterest.com/pin/create/button/?url=${encodeURIComponent(shareUrl)}&description=${encodeURIComponent(message)}`;
    case 'telegram':
      return `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(message)}`;
    case 'linkedin':
      return `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;
    default:
      return '';
  }
}

/**
 * @param {boolean} copied
 */
function setWishlistShareCopiedState(copied) {
  const copiedText =
    (window.Theme && window.Theme.translations && window.Theme.translations.wishlist_share_copied) ||
    'Link copied';
  const copyText =
    (window.Theme && window.Theme.translations && window.Theme.translations.wishlist_share_copy) ||
    'Copy link';

  document.querySelectorAll('[data-zoh-wishlist-copy-label]').forEach((node) => {
    if (node instanceof HTMLElement) {
      node.textContent = copied ? copiedText : copyText;
    }
  });

  document.querySelectorAll('[data-zoh-wishlist-share-status]').forEach((node) => {
    if (!(node instanceof HTMLElement)) return;
    node.textContent = copied ? copiedText : '';
    node.classList.toggle('visually-hidden', !copied);
  });
}

/**
 * @param {Array<Record<string, string>>} items
 */
function updateWishlistShareActions(items) {
  const shareUrl = buildWishlistShareUrl(items);
  const message = getWishlistShareMessage();
  const canShare = items.length > 0;

  document.querySelectorAll('[data-zoh-wishlist-share]').forEach((block) => {
    if (!(block instanceof HTMLElement)) return;
    block.hidden = !canShare;
    if (!canShare || !shareUrl) return;

    block.querySelectorAll('[data-zoh-wishlist-share-platform]').forEach((el) => {
      const platform = el.getAttribute('data-zoh-wishlist-share-platform');
      if (!platform || !(el instanceof HTMLAnchorElement)) return;
      const href = buildSocialShareHref(platform, shareUrl, message);
      if (href) el.href = href;
    });
  });
}

async function copyWishlistShareLink() {
  const shareUrl = buildWishlistShareUrl(zohWishlistRead());
  if (!shareUrl) return;

  try {
    await navigator.clipboard.writeText(shareUrl);
  } catch {
    const textarea = document.createElement('textarea');
    textarea.value = shareUrl;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'absolute';
    textarea.style.left = '-9999px';
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    textarea.remove();
  }

  setWishlistShareCopiedState(true);
  window.setTimeout(() => setWishlistShareCopiedState(false), 2000);
}

/**
 * @param {number} cents
 * @returns {string}
 */
function formatWishlistMoney(cents) {
  const amount = Number(cents) / 100;
  const currency =
    (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';

  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
    }).format(amount);
  } catch {
    return String(amount);
  }
}

/**
 * @param {Record<string, unknown>} product
 * @returns {Record<string, string>}
 */
function mapProductJsToWishlistItem(product) {
  const handle = String(product.handle || '');
  const featured = product.featured_image;
  const image =
    typeof featured === 'string'
      ? featured
      : featured && typeof featured === 'object' && 'src' in featured
        ? String(featured.src)
        : '';

  return {
    id: String(product.id || ''),
    handle,
    title: String(product.title || ''),
    url: handle ? `/products/${handle}` : '#',
    image,
    price: formatWishlistMoney(Number(product.price) || 0),
    compare_at_price: product.compare_at_price
      ? formatWishlistMoney(Number(product.compare_at_price))
      : '',
  };
}

/**
 * Load wishlist products from ?share=handle1,handle2.
 * Shows on the wishlist page when present; otherwise opens the drawer.
 */
async function loadSharedWishlistFromUrl() {
  const shareParam = new URLSearchParams(window.location.search).get('share');
  if (!shareParam) return;

  const handles = shareParam
    .split(',')
    .map((handle) => handle.trim())
    .filter(Boolean);

  if (!handles.length) return;

  const results = await Promise.all(
    handles.map(async (handle) => {
      try {
        const response = await fetch(`/products/${encodeURIComponent(handle)}.js`);
        if (!response.ok) return null;
        const product = await response.json();
        return mapProductJsToWishlistItem(product);
      } catch {
        return null;
      }
    })
  );

  sharedWishlistItems = results.filter(Boolean);
  sharedWishlistItems = filterMembersWishlistItems(sharedWishlistItems);
  if (!sharedWishlistItems.length) return;

  const page = document.querySelector('[data-zoh-wishlist-page]');
  if (page instanceof HTMLElement) {
    page.setAttribute('data-zoh-wishlist-shared', '');
  }

  renderWishlistGrids(document);

  if (!(page instanceof HTMLElement)) {
    const drawer = document.getElementById('zoh-wishlist-drawer');
    if (drawer && typeof drawer.open === 'function') {
      drawer.open();
    }
  }
}

/**
 * @param {string} value
 */
function escapeHtml(value) {
  return String(value)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}

/**
 * @param {string} value
 */
function escapeAttr(value) {
  return escapeHtml(value).replaceAll('`', '&#96;');
}

/**
 * @param {ParentNode} root
 */
function renderWishlistGrids(root = document) {
  const ownItems = zohWishlistRead();
  const grids = root.querySelectorAll('[data-zoh-wishlist-grid]');

  updateWishlistShareActions(ownItems);

  grids.forEach((grid) => {
    if (!(grid instanceof HTMLElement)) return;

    const pageScope = grid.closest('[data-zoh-wishlist-page]');
    const drawerScope = grid.closest('[data-zoh-wishlist-drawer]');
    const scope = pageScope || drawerScope || grid.parentElement;
    const empty = scope?.querySelector('[data-zoh-wishlist-empty]');
    const hasShared = Boolean(sharedWishlistItems && sharedWishlistItems.length);
    const useSharedOnPage = Boolean(pageScope && hasShared);
    const useSharedOnDrawer =
      Boolean(drawerScope && hasShared) && !document.querySelector('[data-zoh-wishlist-page]');
    const useShared = useSharedOnPage || useSharedOnDrawer;
    const rawItems = useShared ? sharedWishlistItems : ownItems;
    const items = filterMembersWishlistItems(rawItems || []);

    grid.replaceChildren();

    if (!items || items.length === 0) {
      if (empty instanceof HTMLElement) empty.hidden = false;
      return;
    }

    if (empty instanceof HTMLElement) empty.hidden = true;

    const fragment = document.createDocumentFragment();
    items.forEach((item) => {
      fragment.appendChild(createWishlistCard(item, { hideRemove: useShared }));
    });
    grid.appendChild(fragment);
  });

  const countLabel = document.querySelector('[data-zoh-wishlist-count-label]');
  if (countLabel instanceof HTMLElement) {
    const label =
      (window.Theme && window.Theme.translations && window.Theme.translations.wishlist_count) ||
      'Wishlist items';
    const visibleOwn = filterMembersWishlistItems(ownItems);
    const count = sharedWishlistItems
      ? filterMembersWishlistItems(sharedWishlistItems).length
      : visibleOwn.length;
    countLabel.textContent = `${label}: ${count}`;
  }
}

/**
 * @param {MouseEvent} event
 */
function onDocumentClick(event) {
  const target = event.target;
  if (!(target instanceof Element)) return;

  const shareControl = target.closest('[data-zoh-wishlist-share-platform]');
  if (shareControl instanceof HTMLElement) {
    const platform = shareControl.getAttribute('data-zoh-wishlist-share-platform');
    if (platform === 'copy' || platform === 'instagram') {
      event.preventDefault();
      event.stopPropagation();
      copyWishlistShareLink();
      return;
    }
  }

  const removeButton = target.closest('[data-zoh-wishlist-remove]');
  if (removeButton instanceof HTMLButtonElement) {
    event.preventDefault();
    event.stopPropagation();
    const productId = removeButton.getAttribute('data-product-id');
    if (productId) zohWishlistRemove(productId);
    return;
  }

  const wishlistButton = target.closest(WISHLIST_BUTTON_SELECTOR);
  if (wishlistButton instanceof HTMLButtonElement) {
    event.preventDefault();
    event.stopPropagation();
    zohWishlistToggle(wishlistButton);
  }
}

function onWishlistChange() {
  updateHeaderBubble();
  renderWishlistGrids(document);
}

function initWishlist() {
  if (window.__zohWishlistBound) return;
  window.__zohWishlistBound = true;

  document.addEventListener('click', onDocumentClick, true);
  document.addEventListener(WISHLIST_EVENT, onWishlistChange);

  syncWishlistButtons(document);
  updateHeaderBubble();
  renderWishlistGrids(document);
  loadSharedWishlistFromUrl();

  if (typeof MutationObserver !== 'undefined') {
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.addedNodes.length > 0) {
          syncWishlistButtons(document);
          break;
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initWishlist, { once: true });
} else {
  initWishlist();
}
